import { EncounterRegionNoteModel } from 'src/app/api/proxy/practice/models/encounter-region-note-model';
import { ObservationModel } from 'src/app/api/proxy/practice/models/observation-model';
import { PartFindingModel } from 'src/app/api/proxy/practice/models/part-finding-model';
import { PartFindingAnatomyRegionType } from 'src/app/api/proxy/practice/models/part-finding-anatomy-region-type';
import { PartType } from 'src/app/api/proxy/practice/models/part-type';
import { TreatmentNoteModel } from 'src/app/api/proxy/practice/models/treatment-note-model';
import { compareNoteRegions, noteRegionToPartFindingAnatomyRegion } from 'src/app/services/anatomy/note-region.util';

export const PART_KEY_TO_TYPE: Record<string, PartType> = {
  pain: PartType.Pain,
  asymmetry: PartType.Asymmetry,
  rangeOfMotion: PartType.Range,
  tissueTone: PartType.Tissue,
};

export const LETTER_TO_PART_KEY: Record<string, keyof typeof PART_KEY_TO_TYPE> = {
  P: 'pain',
  A: 'asymmetry',
  R: 'rangeOfMotion',
  T: 'tissueTone',
};

export function appendPartFindingLetter(
  current: string | null | undefined,
  letter: string,
): string {
  const letters = (current ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
  if (!letters.includes(letter)) {
    letters.push(letter);
  }
  return letters.join(',');
}

export function appendTypeOnlyFinding(
  findings: PartFindingModel[] | null | undefined,
  letter: string,
): PartFindingModel[] {
  const partKey = LETTER_TO_PART_KEY[letter];
  const partType = partKey ? PART_KEY_TO_TYPE[partKey] : undefined;
  const next = [...(findings ?? [])];
  if (partType && !next.some((finding) => finding.partType === partType)) {
    next.push({ partType, observationIds: [] });
  }
  return next;
}

export function removePartFindingLetter(
  current: string | null | undefined,
  letter: string,
): string | null {
  const letters = (current ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0 && item !== letter);
  return letters.length ? letters.join(',') : null;
}

export function removeFindingForLetter(
  findings: PartFindingModel[] | null | undefined,
  letter: string,
): PartFindingModel[] {
  const targetKey = LETTER_TO_PART_KEY[letter];
  if (!targetKey) {
    return [...(findings ?? [])];
  }

  return (findings ?? []).filter((finding) => partTypeToPartKey(finding.partType) !== targetKey);
}

export function partEntryHasFindings(obj: unknown): boolean {
  if (!obj || typeof obj !== 'object') {
    return false;
  }

  const entry = obj as { options?: unknown; dictatedNotes?: unknown; recorded?: unknown };
  const options = entry.options;
  const hasOptions = Array.isArray(options) ? options.length > 0 : !!options;
  return hasOptions || !!entry.dictatedNotes || entry.recorded === true;
}

export function observationIdsFromEntry(entry: unknown): string[] {
  if (!entry || typeof entry !== 'object') {
    return [];
  }

  const options = (entry as { options?: unknown }).options;
  if (!Array.isArray(options)) {
    return [];
  }

  return options.filter((id): id is string => typeof id === 'string' && id.length > 0);
}

export function collectPartFindingsFromPartData(
  partData: Record<string, unknown> | null | undefined,
  composeDescription: (partKey: string, entry: unknown) => string,
): Array<{ partKey: string; description: string; observationIds: string[] }> {
  if (!partData) {
    return [];
  }

  return ['pain', 'asymmetry', 'rangeOfMotion', 'tissueTone']
    .filter((key) => partEntryHasFindings(partData[key]))
    .map((partKey) => ({
      partKey,
      description: composeDescription(partKey, partData[partKey]),
      observationIds: observationIdsFromEntry(partData[partKey]),
    }));
}

export const PART_TYPE_TO_KEY: Record<string, string> = {
  Pain: 'pain',
  Asymmetry: 'asymmetry',
  Range: 'rangeOfMotion',
  Tissue: 'tissueTone',
  pain: 'pain',
  asymmetry: 'asymmetry',
  range: 'rangeOfMotion',
  tissue: 'tissueTone',
  '1': 'pain',
  '2': 'asymmetry',
  '3': 'rangeOfMotion',
  '4': 'tissueTone',
};

export const SOAP_PART_KEYS = ['pain', 'asymmetry', 'rangeOfMotion', 'tissueTone'] as const;
export type SoapPartKey = (typeof SOAP_PART_KEYS)[number];

export interface SoapPartEntry {
  options: string[];
  /** @deprecated Legacy per-part notes — migrated to treatmentNote / regionNote on save. */
  dictatedNotes?: string;
  recorded?: boolean;
}

export interface SoapTreatmentPartState {
  partData: Record<SoapPartKey, SoapPartEntry>;
  treatmentNote: string;
}

export interface SoapRegionPartState {
  partData: Record<SoapPartKey, SoapPartEntry>;
  regionNote: string;
}

export interface SoapAdjustmentPulse {
  side?: string | null;
  vector?: string | null;
  dose?: number | null;
  delivered?: boolean;
}

export interface SoapLevelForGrouping {
  noteRegion: string;
  regionId?: number | null;
  level: string;
  /** Anatomy playlist row side (L/R/X), distinct from per-pulse adjustment side. */
  anatomySide?: string | null;
  id: string;
  playlistIndex: number;
  treatmentId?: string | null;
  treatmentNote?: string | null;
  partFindings?: string | null;
  findings?: PartFindingModel[] | null;
  partIndex?: number;
  adjustments?: SoapAdjustmentPulse[];
}

export interface NoteRegionGroup {
  noteRegion: string;
  apiRegion: PartFindingAnatomyRegionType | null;
  levels: SoapLevelForGrouping[];
}

export function emptyPartData(): Record<SoapPartKey, SoapPartEntry> {
  return {
    pain: { options: [], recorded: false },
    asymmetry: { options: [], recorded: false },
    rangeOfMotion: { options: [], recorded: false },
    tissueTone: { options: [], recorded: false },
  };
}

export function emptyTreatmentPartState(): SoapTreatmentPartState {
  return { partData: emptyPartData(), treatmentNote: '' };
}

export function emptyRegionPartState(): SoapRegionPartState {
  return { partData: emptyPartData(), regionNote: '' };
}

export function normalizePartData(partData: unknown): Record<string, SoapPartEntry> {
  const source = partData && typeof partData === 'object' ? (partData as Record<string, unknown>) : {};
  const normalized = emptyPartData();

  for (const key of SOAP_PART_KEYS) {
    const entry = source[key];
    if (!entry || typeof entry !== 'object') {
      continue;
    }

    const typed = entry as { options?: unknown; dictatedNotes?: unknown; recorded?: unknown };
    normalized[key] = {
      options: Array.isArray(typed.options)
        ? typed.options.filter((id): id is string => typeof id === 'string' && id.length > 0)
        : [],
      ...(typeof typed.dictatedNotes === 'string' && typed.dictatedNotes
        ? { dictatedNotes: typed.dictatedNotes }
        : {}),
      recorded: typed.recorded === true,
    };
  }

  return normalized;
}

/** Keep findings whose description belongs to this region/level. Type-only findings stay. */
export function filterFindingsForLevel(
  findings: PartFindingModel[] | null | undefined,
  region: string,
  levelName: string,
): PartFindingModel[] {
  if (!findings?.length) {
    return [];
  }

  const locationPrefix = [region, levelName].map((value) => value?.trim()).filter(Boolean).join(' ');
  if (!locationPrefix) {
    return findings.slice();
  }

  const belongsToLevel = (description: string): boolean =>
    description.startsWith(`${locationPrefix} `) || description.startsWith(`${locationPrefix} - `);

  const hasLocationStampedFindings = findings.some((finding) =>
    (finding.description || '').includes(' - '),
  );

  return findings.filter((finding) => {
    const description = (finding.description || '').trim();
    if (!description || !description.includes(' - ')) {
      return !hasLocationStampedFindings;
    }

    return belongsToLevel(description);
  });
}

export function assessmentLinePrefix(
  region: string,
  levelName: string,
  partName: string,
  anatomySide?: string | null,
): string | null {
  const level = formatLevelWithOptionalSide(levelName, anatomySide);
  const part = (partName || '').trim();
  if (!level || !part) {
    return null;
  }

  const location = [(region || '').trim(), level, part].filter(Boolean).join(' ');
  return `${location} - `;
}

export function composeAssessmentNotes(
  rows: Array<{
    region?: string | null;
    level?: string | null;
    anatomySide?: string | null;
    partValue?: unknown;
  }>,
  parts: Array<{ key: string; label: string }>,
  translateLabel: (label: string) => string,
): string | null {
  const lines: string[] = [];

  for (const row of rows) {
    const region = (row.region || '').trim();
    const levelWithSide = formatLevelWithOptionalSide(row.level ?? '', row.anatomySide);
    const partValue = row.partValue as Record<string, { dictatedNotes?: string } | null> | null | undefined;
    if (!partValue) {
      continue;
    }

    for (const part of parts) {
      const notes = (partValue[part.key]?.dictatedNotes || '').trim();
      if (!notes) {
        continue;
      }

      const partName = translateLabel(part.label);
      const location = [region, levelWithSide, partName].filter(Boolean).join(' ');
      lines.push(`${location} - ${notes}`);
    }
  }

  return lines.length ? lines.join('\n') : null;
}

/** Apply case-level assessment lines only when they are prefixed with this region's level. */
export function applyAssessmentNotesToPartData(
  partData: Record<string, SoapPartEntry> | null,
  assessmentNotes: string | null | undefined,
  region: string,
  levelName: string,
  partLabels: Record<string, string>,
  anatomySide?: string | null,
): Record<string, SoapPartEntry> | null {
  const assessment = (assessmentNotes || '').toString().trim();
  if (!assessment || !(levelName || '').trim()) {
    return partData;
  }

  const lines = assessment.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const merged = normalizePartData(partData);
  let filled = !!partData;

  for (const key of SOAP_PART_KEYS) {
    const entry = merged[key];
    if (entry.dictatedNotes) {
      continue;
    }

    const prefix = assessmentLinePrefix(region, levelName, partLabels[key] || key, anatomySide);
    if (!prefix) {
      continue;
    }

    const line = lines.find((item) => item.startsWith(prefix));
    if (!line) {
      continue;
    }

    entry.dictatedNotes = line.slice(prefix.length).trim();
    entry.recorded = true;
    filled = true;
  }

  return filled ? merged : partData;
}

const CATALOG_OBSERVATION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function catalogObservationIds(ids: string[] | undefined): string[] {
  return (ids ?? []).filter((id) => CATALOG_OBSERVATION_ID.test(id));
}

export function mergeTreatmentPartStates(
  primary: SoapTreatmentPartState | null,
  secondary: SoapTreatmentPartState | null,
): SoapTreatmentPartState {
  if (!primary) {
    return secondary ?? emptyTreatmentPartState();
  }
  if (!secondary) {
    return primary;
  }

  return {
    partData: mergeSoapPartData(primary.partData, secondary.partData) ?? primary.partData,
    treatmentNote: primary.treatmentNote || secondary.treatmentNote || '',
  };
}

/** Combine payload + saved findings so notes and observation checkboxes can come from different sources. */
export function mergeSoapPartData(
  primary: Record<string, SoapPartEntry> | null,
  secondary: Record<string, SoapPartEntry> | null,
): Record<string, SoapPartEntry> | null {
  if (!primary) {
    return secondary;
  }
  if (!secondary) {
    return primary;
  }

  const merged = emptyPartData();
  let filled = false;

  for (const key of SOAP_PART_KEYS) {
    const fromPrimary = primary[key];
    const fromSecondary = secondary[key];
    const primaryCatalogIds = catalogObservationIds(fromPrimary?.options);
    const secondaryOptions = fromSecondary?.options ?? [];
    merged[key] = {
      options: primaryCatalogIds.length ? primaryCatalogIds : secondaryOptions,
      dictatedNotes: fromPrimary?.dictatedNotes || fromSecondary?.dictatedNotes || '',
      recorded: fromPrimary?.recorded === true || fromSecondary?.recorded === true,
    };
    if (partEntryHasFindings(merged[key])) {
      filled = true;
    }
  }

  return filled ? merged : primary;
}

export interface SoapLevelPayloadLookup {
  treatmentId?: string | null;
  id?: string;
  level?: string;
}

export interface SoapLevelForPartData extends SoapLevelPayloadLookup {
  findings?: PartFindingModel[] | null;
}

/** Key order matches save/load: synthetic playlist id, then treatment GUID, then anatomy name. */
export function partDataKeyForLevel(level: SoapLevelPayloadLookup): string | null {
  const key = level.id || level.treatmentId || level.level;
  return key ? String(key) : null;
}

export function buildPartDataPerLevelByTreatmentKey(
  levels: SoapLevelForPartData[],
): Record<string, SoapTreatmentPartState> {
  const map: Record<string, SoapTreatmentPartState> = {};

  for (const level of levels) {
    const key = partDataKeyForLevel(level);
    if (!key) {
      continue;
    }

    map[key] = legacyFindingsToTreatmentState(level.findings);
  }

  return map;
}

/** Later maps win on conflicts; within a key, incoming part rows override earlier rows. */
export function mergePartDataPerLevelMaps(
  ...maps: Array<Record<string, unknown> | null | undefined>
): Record<string, unknown> | null {
  const merged: Record<string, unknown> = {};
  let hasAny = false;

  for (const map of maps) {
    if (!map) {
      continue;
    }

    for (const [key, value] of Object.entries(map)) {
      if (!value || typeof value !== 'object') {
        continue;
      }

      hasAny = true;
      const incoming = normalizeTreatmentPartState(value);
      const existing = merged[key] ? normalizeTreatmentPartState(merged[key]) : null;
      merged[key] = mergeTreatmentPartStates(incoming, existing);
    }
  }

  return hasAny ? merged : null;
}

/**
 * When the user clears every observation for a PART, drop the stale `recorded` flag so
 * P/A/R/T letters disappear from the level row. Type-only findings (never had options)
 * are left unchanged.
 */
export function reconcilePartDataAfterObservationChange(
  partData: unknown,
  previousOptionsByKey: Record<string, string[]>,
): Record<SoapPartKey, SoapPartEntry> {
  const normalized = normalizePartData(partData) as Record<SoapPartKey, SoapPartEntry>;

  for (const key of SOAP_PART_KEYS) {
    const options = normalized[key].options ?? [];
    const previous = previousOptionsByKey[key] ?? [];

    if (previous.length > 0 && options.length === 0) {
      normalized[key] = { ...normalized[key], recorded: false };
    } else if (options.length > 0) {
      normalized[key] = { ...normalized[key], recorded: true };
    }
  }

  return normalized;
}

/** Readonly observation ids already selected on a level/region (must be kept on save). */
export function collectReadonlyObservationIdsByPartKey(
  partData: unknown,
  isReadonlyObservation: (observationId: string, partKey: SoapPartKey) => boolean,
): Partial<Record<SoapPartKey, string[]>> {
  const normalized = normalizePartData(partData) as Record<SoapPartKey, SoapPartEntry>;
  const locks: Partial<Record<SoapPartKey, string[]>> = {};

  for (const key of SOAP_PART_KEYS) {
    const ids = observationIdsFromEntry(normalized[key]).filter((id) =>
      isReadonlyObservation(id, key),
    );
    if (ids.length) {
      locks[key] = ids;
    }
  }

  return locks;
}

/** Re-apply locked readonly observation ids (ionic select may drop disabled options). */
export function mergeLockedReadonlyObservationIds(
  partData: unknown,
  lockedIdsByPartKey: Partial<Record<SoapPartKey, string[]>>,
): Record<SoapPartKey, SoapPartEntry> {
  const normalized = normalizePartData(partData) as Record<SoapPartKey, SoapPartEntry>;

  for (const key of SOAP_PART_KEYS) {
    const locked = lockedIdsByPartKey[key];
    if (!locked?.length) {
      continue;
    }

    const entry = { ...normalized[key] };
    const options = [...new Set([...(entry.options ?? []), ...locked])];
    entry.options = options;
    if (options.length) {
      entry.recorded = true;
    }
    normalized[key] = entry;
  }

  return normalized;
}

export function partDataHasObservationSelection(partData: unknown): boolean {
  const normalized = normalizePartData(partData);
  return SOAP_PART_KEYS.some((key) => (normalized[key]?.options?.length ?? 0) > 0);
}

/** Replace editable observation ids on a target from a multiselect template; preserve dictatedNotes and readonly locks. */
export function applyObservationTemplateToPartData(
  existing: unknown,
  template: unknown,
  readonlyLocks?: Partial<Record<SoapPartKey, string[]>>,
): Record<SoapPartKey, SoapPartEntry> {
  const existingNormalized = normalizePartData(existing) as Record<SoapPartKey, SoapPartEntry>;
  const templateNormalized = normalizePartData(template) as Record<SoapPartKey, SoapPartEntry>;
  const merged = emptyPartData();

  for (const key of SOAP_PART_KEYS) {
    const prior = existingNormalized[key];
    const templateOptions = [...(templateNormalized[key]?.options ?? [])];
    merged[key] = {
      ...prior,
      options: templateOptions,
      recorded: templateOptions.length > 0,
    };
  }

  const withLocks = readonlyLocks
    ? mergeLockedReadonlyObservationIds(merged, readonlyLocks)
    : merged;

  for (const key of SOAP_PART_KEYS) {
    const options = withLocks[key].options ?? [];
    withLocks[key] = {
      ...withLocks[key],
      recorded: options.length > 0,
    };
  }

  return withLocks;
}

/** Replace editable observation ids for a single P/A/R/T key; preserve other keys and dictatedNotes. */
export function applyPartKeyObservationOptions(
  existing: unknown,
  partKey: SoapPartKey,
  optionIds: string[],
  readonlyLocks?: Partial<Record<SoapPartKey, string[]>>,
): Record<SoapPartKey, SoapPartEntry> {
  const existingNormalized = normalizePartData(existing) as Record<SoapPartKey, SoapPartEntry>;
  const merged = { ...existingNormalized } as Record<SoapPartKey, SoapPartEntry>;

  for (const key of SOAP_PART_KEYS) {
    merged[key] = { ...existingNormalized[key] };
  }

  merged[partKey] = {
    ...existingNormalized[partKey],
    options: [...optionIds],
    recorded: optionIds.length > 0,
  };

  const withLocks = readonlyLocks
    ? mergeLockedReadonlyObservationIds(merged, readonlyLocks)
    : merged;

  const entry = withLocks[partKey];
  const options = entry.options ?? [];
  withLocks[partKey] = {
    ...entry,
    recorded: options.length > 0,
  };

  return withLocks;
}

/** Clear editable observation selections for a P/A/R/T part; keep readonly custom ids. */
export function clearPartEntryRetainingReadonlyOptions(
  partData: unknown,
  partLetter: string,
  isReadonlyObservation: (observationId: string, partKey: SoapPartKey) => boolean,
): { partData: Record<SoapPartKey, SoapPartEntry>; readonlyIdsKept: string[] } {
  const partKey = LETTER_TO_PART_KEY[partLetter] as SoapPartKey | undefined;
  const normalized = normalizePartData(partData) as Record<SoapPartKey, SoapPartEntry>;
  if (!partKey) {
    return { partData: normalized, readonlyIdsKept: [] };
  }

  const retained = observationIdsFromEntry(normalized[partKey]).filter((id) =>
    isReadonlyObservation(id, partKey),
  );
  normalized[partKey] = {
    ...normalized[partKey],
    options: retained,
    recorded: retained.length > 0,
  };

  return { partData: normalized, readonlyIdsKept: retained };
}

export function updateFindingsAfterPartLetterRemoval(
  findings: PartFindingModel[] | null | undefined,
  partLetter: string,
  readonlyIdsKept: string[],
): PartFindingModel[] {
  const partKey = LETTER_TO_PART_KEY[partLetter];
  if (!partKey) {
    return [...(findings ?? [])];
  }

  if (readonlyIdsKept.length === 0) {
    return removeFindingForLetter(findings, partLetter);
  }

  const partType = PART_KEY_TO_TYPE[partKey];
  const list = [...(findings ?? [])];
  const index = list.findIndex((finding) => partTypeToPartKey(finding.partType) === partKey);
  const updated: PartFindingModel = {
    ...(index >= 0 ? list[index] : {}),
    partType,
    observationIds: [...readonlyIdsKept],
  };

  if (index >= 0) {
    list[index] = updated;
  } else {
    list.push(updated);
  }

  return list;
}

/** Observation ids the current practitioner may carry into a duplicated visit (catalog + own customs). */
export function allowedObservationIdsFromCatalog(
  catalog: ObservationModel[],
): ReadonlySet<string> {
  const allowed = new Set<string>();
  for (const observation of catalog) {
    if (observation.id) {
      allowed.add(observation.id);
    }
  }
  return allowed;
}

/** Drop another practitioner's custom observations; keep catalog defaults and own customs. */
export function filterPartFindingsForDuplicate(
  findings: PartFindingModel[] | null | undefined,
  allowedObservationIds: ReadonlySet<string>,
): PartFindingModel[] | null {
  if (!findings?.length) {
    return null;
  }

  const filtered: PartFindingModel[] = [];
  for (const finding of findings) {
    const originalIds = (finding.observationIds ?? []).filter((id): id is string => !!id);
    const keptIds = originalIds.filter((id) => allowedObservationIds.has(id));

    if (keptIds.length > 0) {
      filtered.push({
        partType: finding.partType,
        ...(finding.description != null && finding.description !== ''
          ? { description: finding.description }
          : {}),
        observationIds: keptIds,
      });
      continue;
    }

    if (originalIds.length === 0) {
      filtered.push({
        partType: finding.partType,
        ...(finding.description != null && finding.description !== ''
          ? { description: finding.description }
          : {}),
        observationIds: [],
      });
    }
  }

  return filtered.length ? filtered : null;
}

export function filterPartDataForDuplicate(
  partData: unknown,
  allowedObservationIds: ReadonlySet<string>,
): Record<SoapPartKey, SoapPartEntry> {
  const normalized = normalizePartData(partData) as Record<SoapPartKey, SoapPartEntry>;

  for (const key of SOAP_PART_KEYS) {
    const entry = { ...normalized[key] };
    const options = observationIdsFromEntry(entry).filter((id) => allowedObservationIds.has(id));
    entry.options = options;
    if (!options.length && !(entry.dictatedNotes ?? '').trim()) {
      entry.recorded = false;
    } else if (options.length) {
      entry.recorded = true;
    }
    normalized[key] = entry;
  }

  return normalized;
}

export function filterPartDataPerLevelMapForDuplicate(
  map: Record<string, unknown> | null | undefined,
  allowedObservationIds: ReadonlySet<string>,
): Record<string, unknown> | null {
  if (!map || Object.keys(map).length === 0) {
    return null;
  }

  const filtered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(map)) {
    const state = normalizeTreatmentPartState(value);
    state.partData = filterPartDataForDuplicate(state.partData, allowedObservationIds);
    const hasFindings = partDataToPartFindingModels(state.partData).length > 0;
    const hasNote = !!(state.treatmentNote || '').trim();
    if (hasFindings || hasNote) {
      filtered[key] = state;
    }
  }

  return Object.keys(filtered).length ? filtered : null;
}

export function filterRegionPartStateMapForDuplicate(
  map: Record<string, unknown> | null | undefined,
  allowedObservationIds: ReadonlySet<string>,
): Record<string, unknown> | null {
  if (!map || Object.keys(map).length === 0) {
    return null;
  }

  const filtered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(map)) {
    const state = normalizeRegionPartState(value);
    state.partData = filterPartDataForDuplicate(state.partData, allowedObservationIds);
    const hasFindings = partDataToPartFindingModels(state.partData).length > 0;
    const hasNote = !!(state.regionNote || '').trim();
    if (hasFindings || hasNote) {
      filtered[key] = state;
    }
  }

  return Object.keys(filtered).length ? filtered : null;
}

export function filterRegionNotesForDuplicate(
  regionNotes: EncounterRegionNoteModel[] | null | undefined,
  allowedObservationIds: ReadonlySet<string>,
): EncounterRegionNoteModel[] | null {
  if (!regionNotes?.length) {
    return null;
  }

  const filtered = regionNotes
    .map((note) => {
      const partFindings = filterPartFindingsForDuplicate(note.partFindings, allowedObservationIds);
      return {
        partFindingAnatomyRegion: note.partFindingAnatomyRegion,
        description: note.description ?? null,
        partFindings: partFindings?.length ? partFindings : null,
      };
    })
    .filter((note) => !!(note.description ?? '').trim() || note.partFindings?.length);

  return filtered.length ? filtered : null;
}

export function filterDuplicatedPlaylistFindings<T extends {
  partFindings?: string | null;
  findings?: PartFindingModel[] | null;
}>(
  item: T,
  allowedObservationIds: ReadonlySet<string>,
): T {
  const findings = filterPartFindingsForDuplicate(item.findings, allowedObservationIds);
  const partData = partFindingsToPartData(findings);
  const partFindings = partFindingsLettersFromPartData(partData) || null;

  return {
    ...item,
    findings: findings ?? null,
    partFindings,
  };
}

export function partFindingsLettersFromPartData(partData: unknown): string {
  const normalized = normalizePartData(partData);
  const letters: string[] = [];

  if (partEntryHasFindings(normalized['pain'])) {
    letters.push('P');
  }
  if (partEntryHasFindings(normalized['asymmetry'])) {
    letters.push('A');
  }
  if (partEntryHasFindings(normalized['rangeOfMotion'])) {
    letters.push('R');
  }
  if (partEntryHasFindings(normalized['tissueTone'])) {
    letters.push('T');
  }

  return letters.join(',');
}

/** Prefer the synthetic level id, then treatment GUID, then anatomy name. */
export function resolvePartPayloadForLevel(
  parsedDataByLevel: Record<string, unknown> | null | undefined,
  level: SoapLevelPayloadLookup,
): unknown {
  if (!parsedDataByLevel) {
    return undefined;
  }

  if (level.id !== undefined && parsedDataByLevel[level.id] !== undefined) {
    return parsedDataByLevel[level.id];
  }

  if (level.treatmentId && parsedDataByLevel[level.treatmentId] !== undefined) {
    return parsedDataByLevel[level.treatmentId];
  }

  if (level.level && parsedDataByLevel[level.level] !== undefined) {
    return parsedDataByLevel[level.level];
  }

  return undefined;
}

export const PART_KEY_TO_LETTER: Record<string, string> = {
  pain: 'P',
  asymmetry: 'A',
  rangeOfMotion: 'R',
  tissueTone: 'T',
};

export function selectedPartFindingLetters(
  partFindings?: string | null,
  findings?: PartFindingModel[] | null,
): string | null {
  if (partFindings != null) {
    return partFindings
      .split(',')
      .map((item) => item.trim())
      .filter((item) => !!LETTER_TO_PART_KEY[item])
      .join(',');
  }

  if (!findings?.length) {
    return null;
  }

  const letters: string[] = [];
  for (const finding of findings) {
    const partKey = partTypeToPartKey(finding.partType);
    const letter = partKey ? PART_KEY_TO_LETTER[partKey] : null;
    if (letter && !letters.includes(letter)) {
      letters.push(letter);
    }
  }
  return letters.join(',');
}

export function constrainPartDataToSelectedLetters(
  partData: Record<string, SoapPartEntry> | null,
  letters: string | null | undefined,
): Record<string, SoapPartEntry> | null {
  if (letters == null) {
    return partData;
  }

  const allowedKeys = letters
    .split(',')
    .map((item) => item.trim())
    .map((letter) => LETTER_TO_PART_KEY[letter])
    .filter((key): key is keyof typeof PART_KEY_TO_TYPE => !!key);

  if (!allowedKeys.length) {
    return null;
  }

  const source = normalizePartData(partData);
  const constrained = emptyPartData();
  for (const key of allowedKeys) {
    const partKey = key as SoapPartKey;
    constrained[partKey] = {
      ...source[partKey],
      recorded: true,
    };
  }
  return constrained;
}

export function hydratePartDataForLevel(options: {
  payload?: unknown;
  findings?: PartFindingModel[] | null;
  partFindings?: string | null;
  region?: string;
  levelName?: string;
  assessmentNotes?: string | null;
  partLabels?: Record<string, string>;
  anatomySide?: string | null;
  /** Findings already belong to this treatment — do not parse SOAP notes to match them. */
  findingsAreTreatmentScoped?: boolean;
}): Record<string, SoapPartEntry> | null {
  const region = options.region ?? '';
  const levelName = options.levelName ?? '';
  const payload = options.payload;
  const fromPayloadState = payload != null ? normalizeTreatmentPartState(payload) : null;
  const scopedFindings = options.findingsAreTreatmentScoped
    ? (options.findings ?? [])
    : filterFindingsForLevel(options.findings, region, levelName);
  const fromFindingsState = scopedFindings?.length
    ? legacyFindingsToTreatmentState(scopedFindings)
    : null;
  const baseState = mergeTreatmentPartStates(fromPayloadState, fromFindingsState);
  const selectedLetters = selectedPartFindingLetters(options.partFindings, scopedFindings);

  const constrain = (state: SoapTreatmentPartState) => {
    const constrained = selectedLetters != null
      ? constrainPartDataToSelectedLetters(state.partData, selectedLetters)
      : state.partData;
    return constrained ? { ...state, partData: constrained } : state;
  };

  const hasTreatmentSource =
    options.findingsAreTreatmentScoped === true || fromPayloadState != null || fromFindingsState != null;
  const finalState = hasTreatmentSource || !options.assessmentNotes || !options.partLabels
    ? constrain(baseState)
    : constrain({
        ...baseState,
        partData: applyAssessmentNotesToPartData(
          baseState.partData,
          options.assessmentNotes,
          region,
          levelName,
          options.partLabels,
          options.anatomySide,
        ) ?? baseState.partData,
      });

  const hasContent = SOAP_PART_KEYS.some((key) => partEntryHasFindings(finalState.partData[key]));
  return hasContent ? finalState.partData : null;
}

export function partTypeToPartKey(partType: unknown): string | null {
  if (partType == null || partType === '') {
    return null;
  }

  return PART_TYPE_TO_KEY[String(partType)] ?? null;
}

/** Descriptions are saved as "{region} {level} {part} - {notes}". */
export function extractDictatedNotes(description: string | null | undefined): string {
  if (!description) {
    return '';
  }

  const separator = ' - ';
  const index = description.indexOf(separator);
  return (index >= 0 ? description.slice(index + separator.length) : description).trim();
}

export function partFindingsToPartData(
  findings: PartFindingModel[] | null | undefined,
): Record<SoapPartKey, SoapPartEntry> | null {
  if (!findings?.length) {
    return null;
  }

  const partData = emptyPartData();
  let hasAny = false;

  for (const finding of findings) {
    const partKey = partTypeToPartKey(finding.partType) as SoapPartKey | null;
    if (!partKey || !partData[partKey]) {
      continue;
    }

    hasAny = true;
    partData[partKey].options = (finding.observationIds ?? []).filter((id): id is string => !!id);
    partData[partKey].recorded = true;
  }

  return hasAny ? partData : null;
}

function composeLegacyNoteFromPartData(partData: Record<SoapPartKey, SoapPartEntry>): string {
  const lines: string[] = [];
  for (const key of SOAP_PART_KEYS) {
    const notes = (partData[key]?.dictatedNotes || '').trim();
    if (notes) {
      lines.push(notes);
    }
  }
  return lines.join('\n');
}

function stripLegacyDictatedNotes(
  partData: Record<SoapPartKey, SoapPartEntry>,
): Record<SoapPartKey, SoapPartEntry> {
  const stripped = emptyPartData();
  for (const key of SOAP_PART_KEYS) {
    stripped[key] = {
      options: partData[key]?.options ?? [],
      recorded: partData[key]?.recorded === true,
    };
  }
  return stripped;
}

export function normalizeTreatmentPartState(value: unknown): SoapTreatmentPartState {
  if (value && typeof value === 'object' && 'partData' in (value as object)) {
    const state = value as SoapTreatmentPartState;
    return {
      partData: normalizePartData(state.partData) as Record<SoapPartKey, SoapPartEntry>,
      treatmentNote: typeof state.treatmentNote === 'string' ? state.treatmentNote : '',
    };
  }

  const legacyPartData = normalizePartData(value) as Record<SoapPartKey, SoapPartEntry>;
  const legacyNote = composeLegacyNoteFromPartData(legacyPartData);
  return {
    partData: stripLegacyDictatedNotes(legacyPartData),
    treatmentNote: legacyNote,
  };
}

export function normalizeRegionPartState(value: unknown): SoapRegionPartState {
  if (value && typeof value === 'object' && 'partData' in (value as object)) {
    const state = value as SoapRegionPartState;
    return {
      partData: normalizePartData(state.partData) as Record<SoapPartKey, SoapPartEntry>,
      regionNote: typeof state.regionNote === 'string' ? state.regionNote : '',
    };
  }

  const legacyPartData = normalizePartData(value) as Record<SoapPartKey, SoapPartEntry>;
  const legacyNote = composeLegacyNoteFromPartData(legacyPartData);
  return {
    partData: stripLegacyDictatedNotes(legacyPartData),
    regionNote: legacyNote,
  };
}

/** Migrate legacy per-finding descriptions into a single treatment note. */
export function legacyFindingsToTreatmentState(
  findings: PartFindingModel[] | null | undefined,
): SoapTreatmentPartState {
  const partData = partFindingsToPartData(findings) ?? emptyPartData();
  const noteLines = (findings ?? [])
    .map((finding) => extractDictatedNotes(finding.description))
    .filter((line) => line.length > 0);
  return {
    partData,
    treatmentNote: noteLines.join('\n'),
  };
}

export function partDataToPartFindingModels(
  partData: Record<SoapPartKey, SoapPartEntry> | null | undefined,
): PartFindingModel[] {
  if (!partData) {
    return [];
  }

  return SOAP_PART_KEYS.filter((key) => partEntryHasFindings(partData[key])).map((key) => ({
    partType: PART_KEY_TO_TYPE[key],
    observationIds: observationIdsFromEntry(partData[key]),
  }));
}

export function buildTreatmentNoteModel(
  treatmentId: string,
  state: SoapTreatmentPartState,
): TreatmentNoteModel {
  const partFindings = partDataToPartFindingModels(state.partData);
  return {
    treatmentId,
    description: state.treatmentNote?.trim() || null,
    partFindings: partFindings.length ? partFindings : null,
  };
}

export function buildTreatmentNoteModels(
  entries: Array<{ treatmentId: string; state: SoapTreatmentPartState }>,
): TreatmentNoteModel[] {
  return entries
    .filter(({ treatmentId, state }) => {
      const hasNote = !!(state.treatmentNote || '').trim();
      const hasFindings = partDataToPartFindingModels(state.partData).length > 0;
      return !!treatmentId && (hasNote || hasFindings);
    })
    .map(({ treatmentId, state }) => buildTreatmentNoteModel(treatmentId, state));
}

export function buildRegionNoteModels(
  regionStateMap: Record<string, SoapRegionPartState>,
): EncounterRegionNoteModel[] {
  const models: EncounterRegionNoteModel[] = [];

  for (const [noteRegion, state] of Object.entries(regionStateMap)) {
    const apiRegion = noteRegionToPartFindingAnatomyRegion(noteRegion);
    if (!apiRegion) {
      continue;
    }

    const partFindings = partDataToPartFindingModels(state.partData);
    const description = (state.regionNote || '').trim() || null;
    if (!description && !partFindings.length) {
      continue;
    }

    models.push({
      partFindingAnatomyRegion: apiRegion,
      description,
      partFindings: partFindings.length ? partFindings : null,
    });
  }

  return models;
}

export function regionNotesToRegionStateMap(
  regionNotes: EncounterRegionNoteModel[] | null | undefined,
  noteRegionLabelFor: (region: PartFindingAnatomyRegionType) => string,
): Record<string, SoapRegionPartState> {
  const map: Record<string, SoapRegionPartState> = {};
  for (const note of regionNotes ?? []) {
    if (!note.partFindingAnatomyRegion) {
      continue;
    }
    const label = noteRegionLabelFor(note.partFindingAnatomyRegion);
    if (!label) {
      continue;
    }
    map[label] = {
      partData: partFindingsToPartData(note.partFindings) ?? emptyPartData(),
      regionNote: note.description ?? '',
    };
  }
  return map;
}

export function treatmentNoteToTreatmentState(
  treatmentNote: { description?: string | null; partFindings?: PartFindingModel[] | null } | null | undefined,
  legacyFindings?: PartFindingModel[] | null,
): SoapTreatmentPartState {
  if (treatmentNote) {
    const fromNote = partFindingsToPartData(treatmentNote.partFindings) ?? emptyPartData();
    const description = (treatmentNote.description ?? '').trim();
    if (description || partDataToPartFindingModels(fromNote).length) {
      return { partData: fromNote, treatmentNote: treatmentNote.description ?? '' };
    }
  }

  if (legacyFindings?.length) {
    return legacyFindingsToTreatmentState(legacyFindings);
  }

  return emptyTreatmentPartState();
}

export function groupLevelsByNoteRegion(
  levels: SoapLevelForGrouping[],
  sortWithinGroup: (a: SoapLevelForGrouping, b: SoapLevelForGrouping) => number,
): NoteRegionGroup[] {
  const byRegion = new Map<string, SoapLevelForGrouping[]>();

  for (const level of levels) {
    const key = level.noteRegion || 'Unknown';
    const group = byRegion.get(key) ?? [];
    group.push(level);
    byRegion.set(key, group);
  }

  return [...byRegion.entries()]
    .sort(([left], [right]) => compareNoteRegions(left, right))
    .map(([noteRegion, groupedLevels]) => ({
      noteRegion,
      apiRegion: noteRegionToPartFindingAnatomyRegion(noteRegion),
      levels: [...groupedLevels].sort(sortWithinGroup),
    }));
}

const MAX_CAPPED_DOSE = 72;

export function capDose(dose: number | null | undefined): number {
  const value = typeof dose === 'number' ? dose : Number(dose);
  if (!Number.isFinite(value)) {
    return 0;
  }
  return value > MAX_CAPPED_DOSE ? MAX_CAPPED_DOSE : value;
}

export function formatAnatomySide(side: string | null | undefined): string {
  switch ((side ?? '').trim()) {
    case 'L':
      return 'Left';
    case 'R':
      return 'Right';
    case 'X':
      return 'Bilateral';
    default:
      return (side ?? '').trim();
  }
}

/** Left/Right labels for anatomy row side; omits bilateral (X) to match anatomy UI. */
export function anatomySideLabelForDisplay(side: string | null | undefined): string {
  switch ((side ?? '').trim().toUpperCase()) {
    case 'L':
      return 'Left';
    case 'R':
      return 'Right';
    default:
      return '';
  }
}

export function formatLevelWithOptionalSide(level: string, anatomySide?: string | null): string {
  const levelName = (level ?? '').trim();
  const side = anatomySideLabelForDisplay(anatomySide);
  if (!levelName) {
    return side;
  }
  if (!side) {
    return levelName;
  }
  return `${levelName} - ${side}`;
}

export function buildObservationLookup(catalog: ObservationModel[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const observation of catalog) {
    if (observation.id) {
      map.set(observation.id, (observation.description ?? '').trim());
    }
  }
  return map;
}

function isPulseDelivered(pulse: SoapAdjustmentPulse): boolean {
  return pulse.delivered === true || capDose(pulse.dose) > 0;
}

export function formatAdjustmentSegment(
  level: string,
  pulse: SoapAdjustmentPulse,
): string {
  const levelName = (level ?? '').trim();
  const side = formatAnatomySide(pulse.side);
  const dose = capDose(pulse.dose);
  return `${levelName} - ${side} - ${dose}`;
}

export function formatAdjustmentSegments(
  levels: SoapLevelForGrouping[],
  translate: SoapObjectiveTranslate,
): string | null {
  const segments: string[] = [];
  for (const level of levels) {
    const delivered = (level.adjustments ?? []).filter(isPulseDelivered);
    for (const pulse of delivered) {
      segments.push(formatAdjustmentSegment(level.level, pulse));
    }
  }

  if (!segments.length) {
    return null;
  }

  return `${translate('treatment-page.soap.objective.adjustments-intro')} ${segments.join(' ')}`;
}

export function formatRegionalObservations(
  partData: Record<SoapPartKey, SoapPartEntry>,
  lookup: Map<string, string>,
): string {
  const descriptions: string[] = [];

  for (const key of SOAP_PART_KEYS) {
    for (const id of observationIdsFromEntry(partData[key])) {
      const description = lookup.get(id);
      if (description) {
        descriptions.push(description);
      }
    }
  }

  return descriptions.length ? descriptions.map((description) => `${description}.`).join(' ') : '';
}

export function formatLevelObservationEntries(
  partData: Record<SoapPartKey, SoapPartEntry>,
  lookup: Map<string, string>,
): string {
  const entries: string[] = [];

  for (const key of SOAP_PART_KEYS) {
    const letter = PART_KEY_TO_LETTER[key];
    for (const id of observationIdsFromEntry(partData[key])) {
      entries.push(`(${letter}) ${lookup.get(id) ?? id}`);
    }
  }

  return entries.join(' | ');
}

export type SoapObjectiveTranslate = (
  key: string,
  params?: Record<string, string | number>,
) => string;

export interface ComposeSoapObjectiveInput {
  levels: SoapLevelForGrouping[];
  treatmentStates: SoapTreatmentPartState[];
  regionStates: Record<string, SoapRegionPartState>;
  noteRegionGroups: NoteRegionGroup[];
  observationCatalog: ObservationModel[];
  translate: SoapObjectiveTranslate;
}

function ensureTrailingPeriod(text: string): string {
  const trimmed = text.trim();
  if (!trimmed || trimmed.endsWith('.')) {
    return trimmed;
  }
  return `${trimmed}.`;
}

function collectDistinctObservationDescriptions(
  partDataSources: Array<Record<SoapPartKey, SoapPartEntry>>,
  lookup: Map<string, string>,
): string {
  const seenDescriptions = new Set<string>();
  const descriptions: string[] = [];

  for (const partData of partDataSources) {
    for (const key of SOAP_PART_KEYS) {
      for (const id of observationIdsFromEntry(partData[key])) {
        const description = lookup.get(id);
        if (!description || seenDescriptions.has(description)) {
          continue;
        }
        seenDescriptions.add(description);
        descriptions.push(description);
      }
    }
  }

  return descriptions.length ? descriptions.map((description) => `${description}.`).join(' ') : '';
}

function collectLevelNotesForRegion(
  group: NoteRegionGroup,
  input: ComposeSoapObjectiveInput,
): string {
  const notes: string[] = [];

  for (let index = 0; index < input.levels.length; index++) {
    const level = input.levels[index];
    if (level.noteRegion !== group.noteRegion) {
      continue;
    }

    const note = (input.treatmentStates[index]?.treatmentNote ?? '').trim();
    if (note) {
      notes.push(ensureTrailingPeriod(note));
    }
  }

  return notes.join(' ');
}

function formatRegionObjectiveBlock(
  group: NoteRegionGroup,
  input: ComposeSoapObjectiveInput,
  lookup: Map<string, string>,
): string | null {
  const regionState = input.regionStates[group.noteRegion] ?? emptyRegionPartState();
  const partDataSources: Array<Record<SoapPartKey, SoapPartEntry>> = [regionState.partData];

  for (let index = 0; index < input.levels.length; index++) {
    const level = input.levels[index];
    if (level.noteRegion !== group.noteRegion) {
      continue;
    }

    const state = input.treatmentStates[index] ?? emptyTreatmentPartState();
    partDataSources.push(state.partData);
  }

  const observationLine = collectDistinctObservationDescriptions(partDataSources, lookup);
  const regionNote = (regionState.regionNote ?? '').trim();
  const formattedRegionNote = regionNote ? ensureTrailingPeriod(regionNote) : '';
  const levelNotesLine = collectLevelNotesForRegion(group, input);

  if (!observationLine && !formattedRegionNote && !levelNotesLine) {
    return null;
  }

  let contentLine = input.translate('treatment-page.soap.objective.regional-findings-line', {
    region: group.noteRegion,
  });
  const parts = [observationLine, formattedRegionNote, levelNotesLine].filter(Boolean);
  if (parts.length) {
    contentLine += ` ${parts.join(' ')}`;
  }

  return contentLine;
}

export function composeSoapObjectiveNotes(input: ComposeSoapObjectiveInput): string | null {
  const lookup = buildObservationLookup(input.observationCatalog);
  const sections: string[] = [];

  const adjustmentsSection = formatAdjustmentSegments(input.levels, input.translate);
  if (adjustmentsSection) {
    sections.push(adjustmentsSection);
  }

  for (const group of input.noteRegionGroups) {
    const block = formatRegionObjectiveBlock(group, input, lookup);
    if (block) {
      sections.push(block);
    }
  }

  return sections.length ? sections.join('\n\n') : null;
}

export function toPartFindingModels(
  findings: Array<{ partKey: string; description: string; observationIds?: string[] }>,
): PartFindingModel[] {
  return findings
    .map((finding) => {
      const partType = PART_KEY_TO_TYPE[finding.partKey];
      if (!partType) {
        return null;
      }

      return {
        partType,
        description: finding.description || null,
        observationIds: finding.observationIds ?? [],
      } as PartFindingModel;
    })
    .filter((finding): finding is PartFindingModel => finding != null);
}
